import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PromptCard = ({ 
  prompt, 
  isRecommended = false, 
  onCopy, 
  onSave,
  canSave = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const maxLength = 150;
  const shouldTruncate = prompt?.content?.length > maxLength;
  const displayContent = isExpanded || !shouldTruncate 
    ? prompt?.content 
    : `${prompt?.content?.substring(0, maxLength)}...`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(prompt?.content);
      setCopySuccess(true);
      if (onCopy) onCopy(prompt);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleSave = () => {
    if (onSave) onSave(prompt);
  };

  return (
    <div className={`relative bg-card border rounded-lg p-6 transition-smooth hover:shadow-modal ${
      isRecommended 
        ? 'border-primary ring-2 ring-primary/20 bg-primary/5' :'border-border hover:border-primary/30'
    }`}>
      {/* Recommended Badge */}
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
            <Icon name="Star" size={12} />
            <span>Recommended Prompt</span>
          </div>
        </div>
      )}
      {/* Prompt Title */}
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-text-primary mb-2">
          {prompt?.title}
        </h4>
        <div className="flex items-center space-x-2 text-xs text-text-secondary">
          <Icon name="Clock" size={12} />
          <span>Generated {new Date()?.toLocaleDateString()}</span>
          {prompt?.category && (
            <>
              <span>•</span>
              <span className="capitalize">{prompt?.category}</span>
            </>
          )}
        </div>
      </div>
      {/* Prompt Content */}
      <div className="mb-6">
        <p className="text-text-primary leading-relaxed whitespace-pre-wrap">
          {displayContent}
        </p>
        
        {shouldTruncate && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            className="mt-2 p-0 h-auto text-primary hover:text-primary/80"
          >
            {isExpanded ? 'Show Less' : 'See More'}
          </Button>
        )}
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            iconName={copySuccess ? "Check" : "Copy"}
            iconPosition="left"
            className={copySuccess ? 'text-success border-success' : ''}
          >
            {copySuccess ? 'Copied!' : 'Copy'}
          </Button>
          
          {canSave && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSave}
              iconName="Bookmark"
              iconPosition="left"
            >
              Save
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-1 text-xs text-text-secondary">
          <Icon name="Zap" size={12} />
          <span>AI Generated</span>
        </div>
      </div>
    </div>
  );
};

export default PromptCard;